import {Injectable} from "@angular/core";

import {ReportError} from "app/app.service";
import {ApiService} from "app/services/domain/api.service";
import {UsersService} from "app/services/domain/users.service";
import * as Models from "app/services/proxy/model/models";
import {ApiClientErrorCode} from "framework/services/api.client";
import {ErrorService} from "framework/services/error.service";

// ---------------------------------------------------------------------------
// Exported types
// ---------------------------------------------------------------------------

/** Thrown by getView when the requested view ID does not exist or is inaccessible. */
export class ViewNotFoundError extends Error
{
    constructor(public readonly viewId: string)
    {
        super(`View not found: ${viewId}`);
        this.name = "ViewNotFoundError";
        Object.setPrototypeOf(this, ViewNotFoundError.prototype);
    }
}

/** Thrown by getView when the caller does not have permission to access the view (HTTP 403). */
export class ViewForbiddenError extends Error
{
    constructor(public readonly viewId: string)
    {
        super(`Forbidden: ${viewId}`);
        this.name = "ViewForbiddenError";
        Object.setPrototypeOf(this, ViewForbiddenError.prototype);
    }
}

// Re-export model enums under the names the rest of the feature uses.
export {SavedViewModule as FinopsModule, SavedViewVisibility as FinopsViewVisibility} from "app/services/proxy/model/models";

// ---------------------------------------------------------------------------
// Service
// ---------------------------------------------------------------------------

@Injectable()
export class DeepLinkService
{
    private readonly m_cache = new Map<string, Models.SavedViewDetail>();

    constructor(private readonly m_api: ApiService,
                private readonly m_users: UsersService,
                public readonly errors: ErrorService)
    {
        this.m_users.loggedOut.subscribe(() => this.m_cache.clear());
    }

    @ReportError
    async listViews(module: Models.SavedViewModule,
                    dashboardId: string,
                    visibility?: Models.SavedViewVisibility): Promise<Models.SavedViewDetail[]>
    {
        const filters = Models.SavedViewFilter.newInstance({
                                                               module,
                                                               dashboard_id: dashboardId,
                                                               ...(visibility !== undefined ? {visibility} : {})
                                                           });
        const req     = Models.SavedViewListRequest.newInstance({filters});

        const response = await this.m_api.dashboardSavedViewsApi.listSavedViews(req);
        return response?.results ?? [];
    }

    @ReportError
    async createView(req: Models.SavedViewCreateRequest): Promise<Models.SavedViewDetail>
    {
        const view = await this.m_api.dashboardSavedViewsApi.createSavedView(req);
        if (view) this.m_cache.set(view.id, view);
        return view;
    }

    /** Resolves the view by ID. Throws ViewNotFoundError if the ID does not exist. */
    async getView(viewId: string): Promise<Models.SavedViewDetail>
    {
        const cached = this.m_cache.get(viewId);
        if (cached) return cached;

        try
        {
            const view = await this.m_api.dashboardSavedViewsApi.getSavedView(viewId);
            if (view) this.m_cache.set(viewId, view);
            return view;
        }
        catch (e: unknown)
        {
            const code = (e as { code?: string })?.code;
            if (code === ApiClientErrorCode.Permission) throw new ViewForbiddenError(viewId);
            if (code === ApiClientErrorCode.Http) throw new ViewNotFoundError(viewId);
            throw e;
        }
    }

    @ReportError
    async updateView(req: { viewId: string } & Models.SavedViewUpdateRequest): Promise<Models.SavedViewDetail>
    {
        const {
                  viewId,
                  ...body
              } = req;
        try
        {
            const updateReq = Models.SavedViewUpdateRequest.newInstance(body);
            const view      = await this.m_api.dashboardSavedViewsApi.updateSavedView(viewId, updateReq);
            if (view) this.m_cache.set(viewId, view);
            return view;
        }
        catch (e: unknown)
        {
            if ((e as { code?: string })?.code === ApiClientErrorCode.Http)
            {
                throw new ViewNotFoundError(viewId);
            }
            throw e;
        }
    }

    @ReportError
    async deleteView(viewId: string): Promise<void>
    {
        await this.m_api.dashboardSavedViewsApi.deleteSavedView(viewId);
        this.m_cache.delete(viewId);
    }

    buildDeepLinkUrl(viewId: string): string
    {
        return `${window.location.origin}/provider/services/executivedashboard/finops?view=${viewId}`;
    }
}
